<template>
  <div
    class="visit-inspection-custom-question"
    :id="!isPreviousResponse ? `question-${answer.QuestionId ?? id}` : undefined"
  >
    <QuestionHeader
      :questionText="questionText"
      :questionNumber="questionNumber"
      :invalid="invalid"
      :isRequired="answer.IsRequired && !isPreviousResponse && !answer.PreviousResponses?.length"
      :isAnnual="answer.IsAnnual"
      :visitId="isPreviousResponse ? answer.VisitId : undefined"
      :submittedBy="answer.ModifiedBy"
      :submittedDate="answer.ValidFromTime ? new Date(answer.ValidFromTime) : undefined"
    />
    <div class="question-body">
      <div v-if="answer.VisitQuestionType == EnumVisitQuestionType.RadioButton" class="radio-wrapper">
        <RadioButton
          class="radio-button-wrapper"
          v-show="!disableSelection || answer.AnswerValue == option"
          v-for="option in options"
          :key="option"
          :name="`question-rb-${questionId}-${id}`"
          :value="option"
          :checked="answer.AnswerValue == option"
          @change="handleChange"
          :label="option"
          @click="handleRadioButtonClick(option)"
          :disabled="disableSelection"
          :data-cy="`question-rb-${questionId}`"
        />
      </div>

      <div v-else-if="answer.VisitQuestionType == EnumVisitQuestionType.Checkboxes" class="checkbox-wrapper">
        <Checkbox
          class="checkbox-item"
          v-show="!disableSelection || checkboxValues.includes(option)"
          v-for="option in options"
          :key="option"
          :name="`question-cb-${questionId}-${id}`"
          :value="option"
          :checked="checkboxValues.includes(option)"
          @change="onCheckboxChange(option)"
          @click="onCheckboxClicked($event, option)"
          :label="option"
          :disabled="disableSelection"
          :data-cy="`question-cb-${questionId}`"
        />
      </div>

      <div v-else-if="answer.VisitQuestionType == EnumVisitQuestionType.Date" class="date-wrapper">
        <DatePicker
          :value="answer.AnswerValue ? new Date(answer.AnswerValue) : undefined"
          @change="onDateChange($event)"
          :disabled="disableSelection"
          :data-cy="`question-date-${questionId}`"
        />
      </div>

      <div v-else-if="answer.VisitQuestionType == EnumVisitQuestionType.TimeSpan" class="time-wrapper">
        <TimeSpanInput v-model="answer.AnswerValue" :disabled="disableSelection" required />
      </div>

      <InspectionCommentsView
        v-if="answer.Comments?.length"
        :comments="(answer.Comments as any as EirFormComment[]) ?? []"
      />
      <textarea
        v-if="!isPreviousResponse && (answer.VisitQuestionType == EnumVisitQuestionType.Comment || answer.HasComment)"
        :rows="1"
        :cols="20"
        :maxlength="5000"
        placeholder="Enter your comments here..."
        v-model="answer.AnswerText"
      ></textarea>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { VisitInspectionAnswerDisplay } from "@/Domain/VisitInspectionAnswerDisplay";
import { Checkbox, type RadioButtonChangeEvent } from "@progress/kendo-vue-inputs";
import InspectionCommentsView from "./InspectionCommentsView.vue";
import RadioButton from "@/shared/ui/inputs/RadioButton.vue";
import TimeSpanInput from "@/shared/ui/inputs/TimeSpanInput.vue";
import QuestionHeader from "./QuestionHeader.vue";
import { computed, useId } from "vue";
import type { EirFormComment } from "@/Domain/EirFormComment";
import { EnumVisitQuestionType } from "@/Domain/EnumVisitQuestionType";
import { DatePicker, type DatePickerChangeEvent } from "@progress/kendo-vue-dateinputs";
import { datePickerLoseFocus } from "@/shared/helpers/datePickerHelper";

const answer = defineModel<VisitInspectionAnswerDisplay>({ required: true });

const props = defineProps<{
  invalid?: boolean;
  disableSelection?: boolean;
  questionNumber?: number;
  isPreviousResponse?: boolean;
}>();

const id = useId();
const emit = defineEmits<{
  (e: "answerValueChanged", value: string): void;
}>();

// Only applicable for radio button and checkbox question types
const options = computed(() => {
  return answer.value.Options?.length > 0 ? answer.value.Options : ["Yes", "No", "N/A"];
});

const checkboxValues = computed(() => {
  return answer.value.AnswerValue ? answer.value.AnswerValue.split(";") : [];
});

const questionText = computed(() => {
  return answer.value.QuestionText ?? "";
});

const questionId = computed(() => {
  return answer.value.QuestionId ?? "";
});

const handleChange = function (e: RadioButtonChangeEvent) {
  answer.value.AnswerValue = e.value;
  emit("answerValueChanged", e.value);
};

const handleRadioButtonClick = function (option: string) {
  if (answer.value.AnswerValue != option && !props.disableSelection) {
    answer.value.AnswerValue = option;
    emit("answerValueChanged", answer.value.AnswerValue);
  }
};

const onCheckboxChange = function (option: string) {
  if (props.disableSelection) return;

  if (!checkboxValues.value.includes(option)) {
    checkboxValues.value.push(option);
  } else {
    const index = checkboxValues.value.indexOf(option);
    if (index > -1) {
      checkboxValues.value.splice(index, 1);
    }
  }
  answer.value.AnswerValue = checkboxValues.value.join(";");
  emit("answerValueChanged", answer.value.AnswerValue);
};

const onCheckboxClicked = function (e: Event, option: string) {
  // Check if checkbox-item was clicked
  const checkboxItem = (e.target as HTMLElement)?.classList?.contains("checkbox-item");
  if (checkboxItem) onCheckboxChange(option);
};

const onDateChange = function (e: DatePickerChangeEvent) {
  const dateValue = e.target.value;
  answer.value.AnswerValue = dateValue ? dateValue.toLocaleDateString("en-US") : "";
  emit("answerValueChanged", answer.value.AnswerValue);
  datePickerLoseFocus(e);
};
</script>

<style scoped lang="scss">
.visit-inspection-custom-question {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;

  .question-body {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .radio-wrapper {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .radio-button-wrapper {
        width: fit-content;
        padding: 12px 16px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        background-color: #ffffff;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        cursor: pointer;

        &:hover {
          border-color: #0056b3;
          background-color: #f5f9ff;
          box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
        }

        &:has(.k-radio:checked) {
          border-color: #0056b3;
          background-color: #e3f2fd;
          box-shadow: 0 2px 12px rgba(25, 118, 210, 0.15);
        }

        &:has(.k-radio:disabled) {
          cursor: not-allowed;
          opacity: 0.6;
          background-color: #f5f5f5;

          &:hover {
            border-color: #e0e0e0;
            background-color: #f5f5f5;
            box-shadow: none;
          }
        }

        .k-radio-label {
          font-size: 15px;
          font-weight: 500;
          color: #333333;
          cursor: pointer;
          padding-left: 12px;
          user-select: none;
          flex: 1;
        }

        .k-radio {
          cursor: pointer;
          width: 20px;
          height: 20px;
          border: 2px solid #9e9e9e;
          transition: all 0.2s ease;
          flex-shrink: 0;

          &:hover {
            border-color: #0056b3;
          }

          &:checked {
            border-color: #0056b3;
            background-color: #0056b3;
          }

          &:disabled {
            cursor: not-allowed;
          }
        }
      }

      @media print {
        .k-radio:checked, .k-radio.k-checked {
          color: var(--kendo-color-primary, #0d6efd);
        }
      }
    }

    .checkbox-wrapper {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      :deep(.checkbox-item) {
        width: fit-content;
        padding: 12px 16px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        background-color: #ffffff;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        cursor: pointer;

        &:hover {
          border-color: #0056b3;
          background-color: #f5f9ff;
          box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
        }

        &:has(.k-checkbox:checked) {
          border-color: #0056b3;
          background-color: #e3f2fd;
          box-shadow: 0 2px 12px rgba(25, 118, 210, 0.15);
        }

        &:has(.k-checkbox:disabled) {
          cursor: not-allowed;
          opacity: 0.6;
          background-color: #f5f5f5;

          &:hover {
            border-color: #e0e0e0;
            background-color: #f5f5f5;
            box-shadow: none;
          }
        }

        .k-checkbox-label {
          font-size: 15px;
          font-weight: 500;
          color: #333333;
          cursor: pointer;
          padding-left: 12px;
          user-select: none;
          flex: 1;
        }

        .k-checkbox {
          cursor: pointer;
          width: 20px;
          height: 20px;
          border: 2px solid #9e9e9e;
          border-radius: 4px;
          transition: all 0.2s ease;
          flex-shrink: 0;

          &:hover {
            border-color: #0056b3;
          }

          &:checked {
            border-color: #0056b3;
            background-color: #0056b3;
          }

          &:disabled {
            cursor: not-allowed;
          }
        }
      }
    }

    .date-wrapper {
      padding: 4px 0;

      :deep(.k-datepicker) {
        max-width: 250px;

        .k-input-inner {
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 15px;
          transition: all 0.3s ease;

          &:hover {
            border-color: #0056b3;
            background-color: #f5f9ff;
          }

          &:focus {
            border-color: #0056b3;
            box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
          }
        }
      }

    }

    .time-wrapper {
      padding: 4px 0;
    }
  }

  textarea {
    resize: vertical;
    min-height: unset;
    padding: 12px 16px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.3s ease;
    background-color: #ffffff;

    &:hover {
      border-color: #bdbdbd;
    }

    &:focus {
      outline: none;
      border-color: #0056b3;
      background-color: #f5f9ff;
      box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.1);
    }
  }
}
</style>
